import React, { FC, useRef, useState } from 'react';
import './IssueUsageStatisticsCell.scss';
import MaterialIcon from '../common/MaterialIcon';
import { IssueUsage, IssueUsageOption, IssueUsageStatistic, LoansLeft } from './types/IssueUsage';
import { Popup, PopupTheme } from '../components/Popup/Popup';
import { Trans, useTranslation } from 'react-i18next';

export const IssueUsageStatisticsCell: FC<IssueUsage> = issueUsage => {
    switch (issueUsage.type) {
        case IssueUsageOption.IssueUsageStatistic:
            return <IssueUsageStatisticCell {...issueUsage} />;
        case IssueUsageOption.NotDeliveredYet:
            return <small>{issueUsage.label}</small>;
        default:
            return null;
    }
};

export const IssueUsageStatisticCell: FC<IssueUsageStatistic> = ({ loans, loansLeft, usagePercentage, reserves }) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const { t } = useTranslation();

    return (
        <>
            <div
                className="issue-statistics-cell"
                ref={popoverRef}
                onMouseEnter={() => setVisible(true)}
                onMouseLeave={() => setVisible(false)}
            >
                <div>
                    {loans.foreignLoans !== undefined ? (
                        <Trans
                            i18nKey="shop.subscriptions.loansWithForeignLoans"
                            components={{
                                b: <b />
                            }}
                            values={{
                                ownLoans: loans.ownLoans,
                                foreignLoans: loans.foreignLoans
                            }}
                        />
                    ) : (
                        <Trans
                            i18nKey="shop.subscriptions.loansWithoutForeignLoans"
                            components={{
                                b: <b />
                            }}
                            values={{
                                ownLoans: loans.ownLoans
                            }}
                        />
                    )}
                    {(() => {
                        switch (loansLeft.type) {
                            case LoansLeft.LoansLeftUnlimited:
                                return <b>{t('shop.subscriptions.unlimited')}</b>;
                            case LoansLeft.LoansLeftZero:
                                return (
                                    <div className="loans-left-zero">
                                        <b>{t('shop.subscriptions.zeroLeft')}</b> <MaterialIcon icon="warning" />
                                    </div>
                                );
                            default:
                                return (
                                    <b>
                                        {t('shop.subscriptions.loansLeft', {
                                            loansLeft: loansLeft.loansLeft
                                        })}
                                    </b>
                                );
                        }
                    })()}
                </div>
                {usagePercentage ? (
                    <div className="usage-percentage">
                        {t('shop.subscriptions.usagePercentage', {
                            usagePercentage: usagePercentage
                        })}{' '}
                        <MaterialIcon icon="warning" />
                    </div>
                ) : null}
                {reserves ? (
                    <div className="reserves">
                        <b>+ {reserves.ownReserves}</b>{' '}
                        {reserves.foreignReserves ? `+ ${reserves.foreignReserves}` : null} reserves
                    </div>
                ) : null}
            </div>
            <Popup
                target={popoverRef}
                visible={visible}
                placement="top-start"
                theme={PopupTheme.CUSTOM}
                popupClassName="connect-popover"
                onClose={() => setVisible(false)}
            >
                <ShowLoanDetails loans={loans} reserves={reserves} />
            </Popup>
        </>
    );
};

const ShowLoanDetails: FC<Pick<IssueUsageStatistic, 'loans' | 'reserves'>> = ({ loans, reserves }) => {
    const { t } = useTranslation();
    return (
        <>
            <div className="issue-statistics-popover">
                <span className="title">{t('shop.subscriptions.loansDuringCurrentIssuePeriod')}</span>
                <div className="details">
                    <Trans t={t}>
                        {t('shop.subscriptions.yourUsers', {
                            yourUsers: loans.ownLoans
                        })}
                    </Trans>

                    {reserves?.ownReserves ? (
                        <span className="reserves">
                            <b>
                                {t('shop.subscriptions.ownReserves', {
                                    ownReserves: reserves.ownReserves
                                })}
                            </b>
                        </span>
                    ) : null}
                </div>
                {loans.foreignLoans !== undefined ? (
                    <div className="details">
                        <Trans t={t}>
                            {t('shop.subscriptions.usersOnOtherSites', {
                                usersOnOtherSites: loans.foreignLoans
                            })}
                        </Trans>
                        {reserves?.foreignReserves ? (
                            <span className="reserves">
                                <b>
                                    {t('shop.subscriptions.foreignReserves', {
                                        foreignReserves: reserves.foreignReserves
                                    })}
                                </b>
                            </span>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </>
    );
};
